import { getAccessToken } from '@podus/auth';
import { apiUrlSetting, systemKey } from './config';
import type { ApiEnvelope } from './types';
import { isSessionMessage, redirectToSignIn } from './session';

/** API root ending in /api, e.g. http://localhost:9000/api or https://api-v2.podus.org/api */
function apiBase(): string {
    const raw = apiUrlSetting().replace(/\/+$/, '');
    return raw.endsWith('/api') ? raw : `${raw}/api`;
}

/** Server origin, for public files the API returns as relative paths (uploads/file/…) */
export function apiOrigin(): string {
    return apiBase().replace(/\/api$/, '');
}

export class ApiError extends Error {
    constructor(
        public status: number,
        message: string,
        public payload?: unknown,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

const SERVER_ERROR_MESSAGE = 'Something went wrong on our side. Please try again.';

/**
 * The backend puts error text in different places: `response.message` usually, `response.password`
 * for password checks, `response` itself as a string for uploads, or a top-level `message`.
 * A 500 carries the raw exception text in `response`, which is not for users; it stays in the payload.
 */
function errorMessage(body: unknown, status: number): string {
    if (status >= 500) return SERVER_ERROR_MESSAGE;
    if (body && typeof body === 'object') {
        const { response, message } = body as { response?: unknown; message?: unknown };
        if (typeof response === 'string' && response) return response;
        if (response && typeof response === 'object') {
            const r = response as Record<string, unknown>;
            for (const key of ['message', 'password', 'email', 'phoneNumber']) {
                if (typeof r[key] === 'string' && r[key]) return r[key] as string;
            }
        }
        if (typeof message === 'string' && message) return message;
    }
    if (status === 429) return 'Too many attempts. Please wait a minute and try again.';
    return `Request failed (${status}).`;
}

interface RequestOptions {
    signal?: AbortSignal;
    /** Leave out the user's token, for public routes */
    anonymous?: boolean;
    /** Don't send the user to sign in on a 401, e.g. when signing out */
    skipSessionGuard?: boolean;
}

async function request<T>(
    method: 'GET' | 'POST' | 'DELETE',
    path: string,
    { body, form, ...options }: RequestOptions & { body?: unknown; form?: FormData } = {},
): Promise<T> {
    const token = options.anonymous ? null : getAccessToken();
    const headers: Record<string, string> = {
        Authorization: `Bearer ${systemKey()}${token ? `:${token}` : ''}`,
    };
    // FormData sets its own multipart Content-Type with the boundary
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    let res: Response;
    try {
        res = await fetch(`${apiBase()}${path}`, {
            method,
            headers,
            body: form ?? (body !== undefined ? JSON.stringify(body) : undefined),
            signal: options.signal,
        });
    } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') throw err;
        throw new ApiError(0, 'We could not reach Podus. Check your internet connection and try again.');
    }

    const text = await res.text();
    let json: unknown = null;
    try {
        json = text ? JSON.parse(text) : null;
    } catch {
        // Non-JSON body, e.g. the rate limiter's plain-text 429
    }

    if (!res.ok) {
        const message = errorMessage(json ?? text, res.status);
        if (res.status === 401 && !options.anonymous && !options.skipSessionGuard && isSessionMessage(message)) {
            redirectToSignIn();
        }
        throw new ApiError(res.status, message, json);
    }

    return (json as ApiEnvelope<T> | null)?.response as T;
}

/** NEW_PODUS_API client. Each call resolves with the envelope's `response` (usually `{ data, message }`). */
export const api = {
    get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('POST', path, { ...options, body }),
    /** Multipart upload; one request per file on this backend */
    upload: <T>(path: string, form: FormData, options?: RequestOptions) =>
        request<T>('POST', path, { ...options, form }),
};
